// frontend/src/components/ModalDetalhes.js
import React, { useState, useEffect } from "react";
import { FaEdit } from "react-icons/fa";
import ModalEditarAluno from "./ModalEditarAluno";
import "./ModalDetalhes.css";

const ModalDetalhes = ({ aluno, onClose }) => {
  const [modoEditar, setModoEditar] = useState(false);
  const [alunoDetalhado, setAlunoDetalhado] = useState(aluno); // estado local para dados completos
  const [carregando, setCarregando] = useState(true);

  // 🔹 Busca aluno detalhado no backend
  useEffect(() => {
    const fetchAlunoDetalhado = async () => {
      try {
        setCarregando(true);
        const res = await fetch(`/api/alunos/${aluno.codigo_aluno}`);
        if (!res.ok) throw new Error("Erro ao buscar aluno detalhado.");
        const data = await res.json();
        setAlunoDetalhado(data.aluno || aluno);
      } catch (err) {
        console.error("❌ Falha ao carregar aluno detalhado:", err);
        setAlunoDetalhado(aluno);
      } finally {
        setCarregando(false);
      }
    };

    if (aluno?.codigo_aluno) fetchAlunoDetalhado();
  }, [aluno]);

  // 🔹 Aviso caso referência não exista
  useEffect(() => {
    if (!alunoDetalhado?.Pagamentos?.some(p => p.tipo_servico?.toUpperCase() === 'MATRÍCULA')) {
      console.warn(`⚠️ Aluno ${alunoDetalhado.nome_completo} não possui referência de pagamento registrada.`);
    }
    console.log("🧠 Dados do aluno detalhado:", alunoDetalhado);
  }, [alunoDetalhado]);

  const formatarCodigoAluno = (codigo) => {
    const valor = typeof codigo === "string" ? codigo : codigo?.toString();
    if (valor?.length === 8 && valor.startsWith("202")) return valor;
    return valor?.padStart(8, "0");
  };

  const formatarData = (dataIso, incluirHora = false, curto = false) => {
    if (!dataIso) return "Não informada";
    const opcoes = curto
      ? { day: '2-digit', month: 'short', year: 'numeric' }
      : { day: '2-digit', month: 'long', year: 'numeric' };
    const data = new Date(dataIso).toLocaleDateString("pt-PT", opcoes);
    if (incluirHora) {
      const hora = new Date(dataIso).toLocaleTimeString("pt-PT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${data} às ${hora}`;
    }
    return data;
  };

  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return "Desconhecida";
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const m = hoje.getMonth() - nascimento.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nascimento.getDate())) idade--;
    return idade;
  };

  if (modoEditar) {
    return <ModalEditarAluno aluno={alunoDetalhado} onClose={() => setModoEditar(false)} />;
  }

  if (carregando) return <div className="modal"><p>Carregando informações do aluno...</p></div>;

  return (
    <div className="modal">
      <div className="modal-detalhes-content">
        <h3>Detalhes do Pré-registo</h3>

        <div className="detalhes-grid">
          <h4 className="grid-titulo">Dados Pessoais</h4>
          <p><strong>Nome:</strong> {alunoDetalhado.nome_completo || "Não informado"}</p>
          <p><strong>Gênero:</strong> {alunoDetalhado.genero || "Não informado"}</p>
          <p><strong>Data de Nascimento:</strong> {formatarData(alunoDetalhado.data_nascimento)} ({calcularIdade(alunoDetalhado.data_nascimento)} anos)</p>
          <p><strong>Nacionalidade:</strong> {alunoDetalhado.nacionalidade || "Não informada"}</p>
          <p><strong>Naturalidade:</strong> {alunoDetalhado.naturalidade || "Não informada"}</p>
          <p><strong>Tipo de Documento:</strong> {alunoDetalhado.tipo_documento || "Não informado"}</p>
          <p><strong>Número do Documento:</strong> {alunoDetalhado.numero_documento || "Não informado"}</p>
          <p><strong>Email:</strong> {alunoDetalhado.email || "Não informado"}</p>

          <h4 className="grid-titulo">Responsáveis</h4>
          <p><strong>Telefone do Responsável:</strong> {alunoDetalhado.telefone_responsavel || "Não informado"}</p>
          <p><strong>Nome do Pai:</strong> {alunoDetalhado.nome_pai || "Não informado"}</p>
          <p><strong>Telefone do Pai:</strong> {alunoDetalhado.telefone_pai || "Não informado"}</p>
          <p><strong>Nome da Mãe:</strong> {alunoDetalhado.nome_mae || "Não informado"}</p>
          <p><strong>Telefone da Mãe:</strong> {alunoDetalhado.telefone_mae || "Não informado"}</p>

          <h4 className="grid-titulo">Saúde</h4>
          <p className={alunoDetalhado.historico_medico ? "campo-destaque" : ""}><strong>Histórico Médico:</strong> {alunoDetalhado.historico_medico || "Nenhum"}</p>
          <p className={alunoDetalhado.deficiencia ? "campo-destaque" : ""}><strong>Deficiência:</strong> {alunoDetalhado.deficiencia || "Nenhuma"}</p>
          <p className={alunoDetalhado.doenca_cronica ? "campo-destaque" : ""}><strong>Doença Crônica:</strong> {alunoDetalhado.doenca_cronica || "Nenhuma"}</p>
          <p><strong>Outras Condições:</strong> {alunoDetalhado.outras_condicoes || "Nenhuma"}</p>

          <h4 className="grid-titulo">Informações Acadêmicas</h4>
          <p><strong>Código do Aluno:</strong> {formatarCodigoAluno(alunoDetalhado.codigo_aluno) || "Não informado"}</p>
          <p><strong>Turma:</strong> {alunoDetalhado.turma ? String(alunoDetalhado.turma).toUpperCase().replace(/[^A-Z]/g, '') : "Não atribuída"}</p>
          <p><strong>Classe:</strong> {alunoDetalhado.classe || "Não informada"}</p>
          <p><strong>Data da Matrícula:</strong> {formatarData(alunoDetalhado.data_matricula, true)}</p>
          <p><strong>Valor da Matrícula:</strong>{" "}
            {alunoDetalhado.valor_matricula ? Number(alunoDetalhado.valor_matricula).toLocaleString("pt-PT", {
              style: "currency",
              currency: "MZN",
              minimumFractionDigits: 2,
            }) : "Não informado"}
          </p>
          <p><strong>Base Mensal (MZN):</strong>{" "}
            {alunoDetalhado.base_mensal ? Number(alunoDetalhado.base_mensal).toLocaleString("pt-PT", {
              style: "currency",
              currency: "MZN",
              minimumFractionDigits: 2,
            }) : "Não informado"}
          </p>
         <p><strong>Referência Matrícula:</strong> {  
  aluno.Pagamentos?.find(p => p.tipo_servico.toUpperCase() === 'MATRÍCULA')?.referencia || "Não gerada ainda"
}</p>

        </div>

        <div className="botoes-inferiores">
          <button className="btn-editar" onClick={() => setModoEditar(true)}>
            <FaEdit /> Editar
          </button>
          <button className="btn-fechar" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalDetalhes;
